import * as endpoints from "../../lib/api/endpoints";
import * as validationLib from "../../lib/validation";
import ActionButton from "../ActionButton";
import callApplicationLead from "../../lib/callApplicationLead";
import CardBanner from "../CardBanner";
import hasFailingValidation from "../../lib/hasFailingValidation";
import isApplicationKey from "../../lib/isApplicationKey";
import LinedInput, {InputType} from "../LinedInput";
import React, {useEffect, useRef, useState} from "react";
import securedCardImage from "../../images/secured.png";
import securedCardImage2 from "../../images/secured@2x.png";
import securedCardImage3 from "../../images/secured@3x.png";
import SetNoIndex from "../../lib/SetNoIndex";
import styles from "./SecuredCardForgotPage.module.css";
import validateApplicationField from "../../lib/validateApplicationField";
import ValidationList from "../ValidationList";
import {AppKeys} from "../../lib/api/models";

const strings = {
    bannerDescription:
        "To retrieve your secured card application, please enter your email below. We will verify your email address and send a new link to your email account, allowing you to continue your application.",
    email: "Email",
    submissionMessage:
        "Thanks for submitting your email. If this is a valid account we will send an email to you with a link to allow you to continue your application. If you do not receive this email, please call our Customer Service team at 1-800-733-1732.",
    submit: "Submit",
};

function getSecuredCardImage() {
    return (
        <img
            className={styles.cardsImage}
            src={securedCardImage}
            srcSet={`${securedCardImage} 1x, ${securedCardImage2} 2x, ${securedCardImage3} 3x`}
            alt="secured card"
        />
    );
}

export default function SecuredCardForgotPage() {
    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    SetNoIndex();

    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [validation, setValidation] = useState<validationLib.Validation>({});
    const emailHasChanged = useRef(false);

    function clearValidation(validationKey: AppKeys) {
        setValidation((prev) => {
            if (prev.generic || (prev.errors && prev.errors[validationKey])) {
                const nextValidation = {
                    ...prev,
                };
                delete nextValidation.generic;
                if (prev && prev.errors) {
                    delete prev.errors[validationKey];
                }
                return nextValidation;
            }
            return prev;
        });
    }

    function onBlurInput(e: React.FocusEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;
        if (isApplicationKey(name)) {
            if (emailHasChanged.current) {
                callApplicationLead("secured-forgot-email", null, window.location.href, value);
                emailHasChanged.current = false;
            }
            const {isValid, messages} = validateApplicationField(name, value);
            if (isValid) {
                return;
            }

            setValidation((prev) => ({
                ...prev,
                errors: {
                    ...prev?.errors,
                    [name]: messages,
                },
            }));
        }
    }

    function onSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (hasFailingValidation(validation)) {
            return;
        }

        callApplicationLead("secured-forgot-submit", null, window.location.href);

        endpoints
            .forgotSecuredCard(email)
            .then(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setSubmitted(true);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                const errValidation = validationLib.getValidationObj(err);
                setValidation(errValidation);
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoading(false);
            });
    }

    return (
        <CardBanner
            renderOnlyCard={false}
            renderBlueGradient={false}
            cardImage={getSecuredCardImage()}
        >
            {!submitted ? (
                <>
                    <div className={styles.bannerDescription}>{strings.bannerDescription}</div>
                    <form onSubmit={onSubmit}>
                        <LinedInput
                            id={AppKeys.Email}
                            name={AppKeys.Email}
                            label={strings.email}
                            onBlur={onBlurInput}
                            type={InputType.Email}
                            value={email}
                            setValue={(value) => {
                                setEmail(value);
                                clearValidation(AppKeys.Email);
                                emailHasChanged.current = true;
                            }}
                            required={true}
                            maxLength={40}
                            validationMessage={
                                validation.errors ? validation.errors[AppKeys.Email] : undefined
                            }
                        />
                        {validation.generic ? (
                            <ValidationList
                                className={styles.validation}
                                messages={validation.generic}
                            />
                        ) : null}
                        <ActionButton
                            className={styles.submitBtn}
                            fullWidth={true}
                            label={strings.submit}
                            loading={loading}
                            type="submit"
                        />
                    </form>
                </>
            ) : (
                <div className={styles.bannerDescription}>{strings.submissionMessage}</div>
            )}
        </CardBanner>
    );
}
